import { ref, computed } from 'vue'
import axios from '../plugins/axios'

export function useStationData(stationId) {
  const station = ref(null)
  const packets = ref([])
  const statistics = ref([])
  const chartdata = ref({
    labels: [],
    datasets: []
  })
  const packetsMap = ref([])
  const loadingPackets = ref(false)
  const loading = ref(true)
  const error = ref(null)

  // Control de paginación
  const lastPacketLoad = ref(0)
  const packetLoadCount = ref(0)

  const getStation = async () => {
    try {
      loading.value = true
      error.value = null
      const { data } = await axios.get(`/v1/station/${stationId}`)
      
      if (!data.description) data.description = ""
      if (!data.pictures) data.pictures = []
      data.id = stationId
      station.value = data
      document.title = `${data.name} Console - TinyGS`
    } catch (e) {
      error.value = 'Error loading station data'
      console.error(e)
    } finally {
      loading.value = false
    }
  }

  const getStatistics = async () => {
      try {
          error.value = null;
          const { data } = await axios.get(
              `/v1/station/${stationId}/statistics`,
          );

          // Store raw statistics data
          statistics.value = data;

          // Process chart data from the statistics
          const today = new Date();
          today.setUTCHours(0, 0, 0, 0);
          const timeWindow = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
          const startTime = today.getTime() - timeWindow;

          // Create map of days to their data for efficient lookup
          const statsMap = new Map(data.map((stat) => [stat.day, stat]));

          const telemetry = [];
          const frames = [];
          const labels = [];

          // Fill data for last 30 days
          for (
              let currDay = startTime;
              currDay <= today.getTime();
              currDay += 24 * 60 * 60 * 1000
          ) {
              const stat = statsMap.get(currDay);
              labels.push(new Date(currDay).getDate());
              telemetry.push(stat ? stat.telemetryPkt : 0);
              frames.push(stat ? stat.nFrames : 0);
          }

          chartdata.value = {
              labels: labels,
              datasets: [
                  {
                      type: 'bar',
                      label: 'Telemetry packets',
                      backgroundColor: '#1f83e6',
                      data: telemetry,
                      categoryPercentage: 0.6,
                      barPercentage: 1,
                      stack: 'packets',
                  },
                  {
                      type: 'bar',
                      label: 'Total packets',
                      backgroundColor: '#84baf0',
                      data: frames,
                      categoryPercentage: 0.6,
                      barPercentage: 1,
                      stack: 'packets',
                  },
              ],
          };
      } catch (e) {
          error.value = 'Error loading statistics';
          console.error(e);
      }
  }

  const getPackets = async () => {
    try {
      loadingPackets.value = true
      error.value = null

      if (packetLoadCount.value > 3 && new Date() - lastPacketLoad.value < 10000) {
        setTimeout(getPackets, 11000 - (new Date() - lastPacketLoad.value))
        return
      }

      packetLoadCount.value++
      let params = { station: stationId }
      let config = {}

      if (packets.value.length > 0) {
        config = {
          headers: {
            sessionToken: localStorage.sessionToken,
            userId: localStorage.userId
          }
        }
        params.before = packets.value[packets.value.length-1].serverTime
      }

      const { data } = await axios.get(
        `/v1/packets`,
        { params: params, headers: config.headers }
      )

      if (!packets.value.length && Array.isArray(data)) {
        packets.value = data || []
        packetsMap.value = data || []
      } else {
        packets.value.push(...data)
      }

      lastPacketLoad.value = new Date()
    } catch (e) {
      error.value = 'Error loading packets'
      console.error(e)
    } finally {
      loadingPackets.value = false
    }
  }

  const isCurrentUser = (userId) => {
    return localStorage.userId == userId
  }

  const confirmedPictures = computed(() => {
    if (!station.value || !station.value.pictures) return null
    return station.value.pictures.filter(pic => pic.status == 1)
  })

  const updateStationConfig = (config) => {
    if (station.value) {
      station.value.description = config.description
      station.value.antennaBand = config.antennaBand
      station.value.antenna = config.antenna
      station.value.pictures.push(...config.newFiles)
    }
  }

  const addPictures = (newFiles) => {
    if (station.value && station.value.pictures) {
      station.value.pictures.push(...newFiles)
    }
  }

  return {
    // Estado
    station,
    packets,
    statistics,
    chartdata,
    packetsMap,
    loadingPackets,
    loading,
    error,
    
    // Métodos
    getStation,
    getStatistics,
    getPackets,
    isCurrentUser,
    updateStationConfig,
    addPictures,
    
    // Computed
    confirmedPictures
  }
}